import { useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import { FeatureName, UserRole } from '../types';

interface UseFeatureAccessReturn {
  // Check if a feature is enabled for current user's role
  isFeatureEnabled: (feature: FeatureName) => boolean;
  // Check if user can perform actions (not just view)
  canPerformAction: (feature: FeatureName) => boolean;
  // Check if user is in read-only mode for a feature (Admin with disabled feature)
  isReadOnlyMode: (feature: FeatureName) => boolean;
  // Get message for disabled feature action
  getDisabledActionMessage: () => string;
  // Get message for disabled feature
  getDisabledMessage: (feature: FeatureName) => string;
  // Check if user is super admin (full access)
  hasFullAccess: boolean;
  // Check if HR features are available (always true)
  isHRAvailable: boolean;
}

const featureDisplayNames: Record<FeatureName, string> = {
  dashboard: 'Dashboard',
  trades: 'Trading',
  clients: 'Clients',
  riskMonitor: 'Risk Monitor',
  payouts: 'Payouts',
  analytics: 'Analytics',
};

export function useFeatureAccess(): UseFeatureAccessReturn {
  const { isSuperAdmin, user } = useAuth();
  const { currentUserFeatures, isFeatureEnabled: contextIsFeatureEnabled } = useFeatureFlags();

  const isFeatureEnabled = useCallback((feature: FeatureName): boolean => {
    // Super admin always has access
    if (isSuperAdmin) return true;
    // Check feature flag for current user's role
    return contextIsFeatureEnabled(feature);
  }, [isSuperAdmin, contextIsFeatureEnabled]);

  // Check if Admin is in read-only mode (feature disabled but they can still view)
  const isReadOnlyMode = useCallback((feature: FeatureName): boolean => {
    // Super admin is never in read-only mode
    if (isSuperAdmin) return false;
    // Only Admin role gets read-only mode
    const userRoles = user?.roles || (user?.role ? [user.role] : []);
    const isAdmin = userRoles.includes(UserRole.ADMIN);
    if (!isAdmin) return false;
    // If feature is disabled for admin, they're in read-only mode
    return !contextIsFeatureEnabled(feature);
  }, [isSuperAdmin, user, contextIsFeatureEnabled]);

  const canPerformAction = useCallback((feature: FeatureName): boolean => {
    // Super admin can always perform actions
    if (isSuperAdmin) return true;
    // If in read-only mode, cannot perform actions
    const userRoles = user?.roles || (user?.role ? [user.role] : []);
    const isAdmin = userRoles.includes(UserRole.ADMIN);
    if (isAdmin && !contextIsFeatureEnabled(feature)) {
      return false;
    }
    // Check if feature is enabled for current user's role
    return currentUserFeatures[feature] ?? false;
  }, [isSuperAdmin, user, currentUserFeatures, contextIsFeatureEnabled]);

  const getDisabledActionMessage = useCallback((): string => {
    return 'This feature is currently under development. View-only access is available.';
  }, []);

  const getDisabledMessage = useCallback((feature: FeatureName): string => {
    const name = featureDisplayNames[feature];
    return `${name} feature is coming soon. This functionality will be available once the feature is enabled.`;
  }, []);

  return {
    isFeatureEnabled,
    canPerformAction,
    isReadOnlyMode,
    getDisabledActionMessage,
    getDisabledMessage,
    hasFullAccess: isSuperAdmin,
    isHRAvailable: true, // HR is always available
  };
}

export default useFeatureAccess;
